import {
  Certification,
  CertResource,
  SoftwareCategory,
  SoftwareLicenseModel,
  SoftwareTool,
} from '@core/models';

import { CertificationDto, SaveCertificationDto, SaveSoftwareToolDto, SoftwareToolDto } from './catalogs.dto';

/**
 * The one translation for the certification and software catalogues.
 *
 * Mostly defaulting rather than renaming: the app's models were shaped to this
 * contract in the first place. The defaulting is the point — the DTO says
 * `string` because the contract does, and a nullable column reaches the wire as
 * null, so the boundary is where that is absorbed.
 */

export function certificationFromDto(c: CertificationDto): Certification {
  return {
    certificationGuid: c.certificationGuid,
    name: c.name ?? '',
    issuingBody: c.issuingBody ?? '',
    validForMonths: c.validForMonths ?? null,
    requiredToWork: !!c.requiredToWork,
    resources: certResources(c),
    description: c.description ?? '',
    // Absent means active: a row the catalogue returns is one the company uses.
    active: c.active ?? true,
  };
}

/**
 * A certification on the way out. The same body creates and edits.
 *
 * `resources` is replaced wholesale, which is what the page does to them.
 *
 * `details` is NOT sent. No form collects it and no screen shows it, so by the
 * standing rule it is not ours to carry — and since the write applies every
 * field, not sending it clears the column, which is the intended outcome.
 */
export function certificationToDto(c: Certification): SaveCertificationDto {
  return {
    name: c.name,
    issuingBody: c.issuingBody,
    description: c.description,
    requiredToWork: c.requiredToWork,
    // The server refuses zero and below; null is "never expires".
    validForMonths: c.validForMonths && c.validForMonths > 0 ? c.validForMonths : null,
    resources: c.resources.map((r) => ({ label: r.label, url: r.url, kind: r.kind ?? null })),
  };
}

/**
 * Resources, from whichever shape the server is on.
 *
 * `resources[]` is replacing a single `resourceUrl` (§5.1) and the contract
 * describes that as in progress. Reading only the new one would render nothing
 * at all against a server still sending the old.
 */
function certResources(c: CertificationDto): CertResource[] {
  if (c.resources?.length) {
    return c.resources.map((r) => ({ label: r.label ?? '', url: r.url ?? '', kind: r.kind ?? 'RESOURCE' }));
  }
  return c.resourceUrl ? [{ label: 'Where to complete it', url: c.resourceUrl, kind: 'COMPLETION' }] : [];
}

export function softwareToolFromDto(s: SoftwareToolDto): SoftwareTool {
  return {
    softwareGuid: s.softwareGuid,
    name: s.name ?? '',
    vendor: s.vendor ?? '',
    category: (s.category ?? 'OTHER') as SoftwareCategory,
    url: s.url ?? '',
    licenseModel: (s.licenseModel ?? 'PER_SEAT') as SoftwareLicenseModel,
    // Seats only mean anything for per-seat licensing.
    seatsTotal: s.licenseModel === 'PER_SEAT' ? s.seatsTotal ?? null : null,
    annualCostPennies: s.annualCostPennies ?? null,
    description: s.description ?? '',
    notes: s.notes ?? '',
    active: s.active ?? true,
  };
}

export function softwareToolToDto(s: SoftwareTool): SaveSoftwareToolDto {
  return {
    name: s.name,
    vendor: s.vendor,
    category: s.category,
    url: s.url,
    licenseModel: s.licenseModel,
    seatsTotal: s.licenseModel === 'PER_SEAT' ? s.seatsTotal : null,
    annualCostPennies: s.annualCostPennies,
    description: s.description,
    notes: s.notes,
  };
}
